/**
 * The sign-up consents as four separate boxes - sign-up, the one-time screen
 * for existing accounts, and Settings all show the same texts, so a traveller
 * reads the same wording the server stores a version of.
 *
 * Nothing is pre-ticked: Article 7 and Recital 32 GDPR do not accept a
 * pre-ticked box as consent.
 */

import { useI18nStore } from "@/stores/i18nStore";
import type { CoreConsent, CoreDecisions } from "@/utils/coreConsents";
import React from "react";
import { Link } from "react-router-dom";

interface Props {
  value: CoreDecisions;
  onChange: (next: CoreDecisions) => void;
  /** Settings leaves the Terms out: they are declined by deleting the account. */
  showTerms?: boolean;
  disabled?: boolean;
}

/** "{terms}" and "{privacy}" in the sentence become links to the pages. */
function TermsSentence() {
  const { t } = useI18nStore();
  const link = (to: string, label: string) => (
    <Link
      key={to}
      to={to}
      target="_blank"
      rel="noopener"
      className="font-semibold text-primary-600 underline"
    >
      {label}
    </Link>
  );
  return (
    <>
      {t.consentTerms
        .split(/(\{terms\}|\{privacy\})/)
        .map((part, i) =>
          part === "{terms}" ? (
            link("/terms", t.consentTermsLink)
          ) : part === "{privacy}" ? (
            link("/privacy", t.privacyPolicy)
          ) : (
            <React.Fragment key={i}>{part}</React.Fragment>
          ),
        )}
    </>
  );
}

export const ConsentChoices: React.FC<Props> = ({
  value,
  onChange,
  showTerms = true,
  disabled = false,
}) => {
  const { t } = useI18nStore();

  const rows: Array<{
    type: CoreConsent;
    label: React.ReactNode;
    hint?: string;
    optional: boolean;
  }> = [
    ...(showTerms
      ? [
          {
            type: "TERMS_OF_SERVICE" as const,
            label: <TermsSentence />,
            optional: false,
          },
        ]
      : []),
    {
      type: "HEALTH_DATA",
      label: t.consentHealth,
      hint: t.consentHealthWithout,
      optional: true,
    },
    {
      type: "AI_PROCESSING",
      label: t.consentAi,
      hint: t.consentAiWithout,
      optional: true,
    },
    {
      type: "MARKETING_COMMUNICATIONS",
      label: t.consentMarketing,
      optional: true,
    },
  ];

  return (
    <div className="space-y-3">
      {rows.map(({ type, label, hint, optional }) => {
        const id = `consent-${type}`;
        return (
          <div key={type} className="flex items-start gap-3">
            <input
              id={id}
              type="checkbox"
              checked={value[type] === true}
              disabled={disabled}
              onChange={(e) => onChange({ ...value, [type]: e.target.checked })}
              className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-primary-600"
            />
            <label
              htmlFor={id}
              className="cursor-pointer text-[13px] leading-normal text-slate-600"
            >
              {optional && (
                <span className="mr-1 font-semibold text-ink">
                  {t.consentOptional}:
                </span>
              )}
              {label}
              {hint && (
                <span className="mt-0.5 block text-[12px] text-slate-500">
                  {hint}
                </span>
              )}
            </label>
          </div>
        );
      })}
    </div>
  );
};

export default ConsentChoices;
